import { z } from "zod";

/**
 * The field-type descriptor contract, per docs/04-BUILD-PLAN.md Phase 3.
 *
 * `FormField.type` is a plain String in the database, not a Prisma enum, precisely so that adding
 * a field type never needs a migration (docs/02-DATA-MODEL.md design decision #3). This registry
 * is the authoritative list instead — the builder palette is served from it (`GET /field-types`)
 * so the UI can never drift from what the validator actually accepts.
 */

export const FIELD_CATEGORIES = [
  "layout",
  "basic_info",
  "text",
  "number",
  "choice",
  "matrix",
  "date_time",
  "rating",
  "legal",
  "identifier",
  "system",
  "lookup",
] as const;

export type FieldCategory = (typeof FIELD_CATEGORIES)[number];

/**
 * How answers to this field are ordered when a coordinator sorts the submissions table by it.
 *
 * Answers live in one `jsonb` column, so `data->>'key'` hands SQL a string whatever the field is
 * and every sort was therefore a *string* sort: 10 landed between 1 and 2, and a date typed in a
 * form ordered by its leading digits. The kind is declared here, beside the `valueSchema` that
 * decides what a stored answer looks like, so the two cannot drift apart.
 *
 * `text` is the default and covers more than it looks like it does. `month_year` stores `YYYY-MM`
 * and `time` stores `HH:MM`; both are already chronological read left to right, and casting them
 * to a timestamp would only turn every value into NULL.
 */
export type FieldSortKind = "numeric" | "temporal" | "text";

/**
 * The stored shape of one field, as far as the registry is concerned. A subset of the `FormField`
 * row in packages/db — deliberately not imported from there, because this package is also bundled
 * into the browser, which has no database client.
 */
export interface FieldDefinition {
  key: string;
  type: string;
  label: string;
  isRequired: boolean;
  visibility: "visible" | "hidden" | "admin_only";
  config: Record<string, unknown>;
  sectionKey?: string | null;
  pageNumber?: number;
}

/** Hints for how a renderer should present the field. Advisory — never load-bearing for validation. */
export interface RenderHints {
  /** Occupies its own row and takes no answer (headings, dividers, page breaks). */
  readonly isLayoutOnly?: boolean;
  /** Never rendered to an applicant at all (identifiers, utm capture). */
  readonly isHiddenFromApplicant?: boolean;
  /** Renders as several sub-inputs under one label (`name`, `address`, `ng_state_lga`). */
  readonly isComposite?: boolean;
  /** Answer options come from the server, not the field config (`large_list`, `ng_state_lga`). */
  readonly isServerBacked?: boolean;
  /** Applicant cannot edit it; the value is derived (`formula`). */
  readonly isReadOnly?: boolean;
  readonly multiline?: boolean;
}

/**
 * `valueSchema` is a factory, not a static schema: what counts as a valid answer depends on the
 * field's own configuration (a `number` with min 0 max 10, a `multi_select` with minSelections 2).
 * `isRequired` is passed separately because conditional logic can override a field's stored
 * requiredness at validation time — see `logic/evaluate.ts`.
 */
export interface FieldDescriptor<
  TConfig extends Record<string, unknown> = Record<string, unknown>,
> {
  readonly type: string;
  readonly label: string;
  readonly category: FieldCategory;
  /** Lucide icon name — the dashboard already depends on lucide-react. */
  readonly icon: string;
  /**
   * Input is `unknown`, not `TConfig`: stored config arrives as untyped JSONB, and most of these
   * schemas use `.default()`, whose input type legitimately differs from its output type. Parsing
   * is what turns the one into the other.
   */
  readonly configSchema: z.ZodType<TConfig, z.ZodTypeDef, unknown>;
  readonly valueSchema: (config: TConfig, isRequired: boolean) => z.ZodTypeAny;
  readonly defaultConfig: TConfig;
  readonly supportsProfileBinding: boolean;
  /**
   * For composite fields, how the parts of the stored object map onto profile attributes.
   *
   * A composite answer is one object — `{ firstName, lastName }` — but the profile is a flat set of
   * scalar attributes, because that is what carries across forms: next year's form may ask for the
   * name with two separate text inputs and must still prefill. Without this, binding a composite
   * field wrote the whole object into a single attribute slot, so `person.first_name` held
   * `{firstName: "...", lastName: "..."}` and anything reading it as a string — `displayName`, a
   * plain text field on another form — got an object.
   *
   * Keys are the composite value's own keys; values are profile attribute keys.
   */
  readonly profileParts?: Readonly<Record<string, string>>;
  readonly defaultSearchable: boolean;
  /** Omitted means `text` — see `FieldSortKind`. */
  readonly sortKind?: FieldSortKind;
  readonly renderHints: RenderHints;
}

/**
 * A registry entry with its config type erased.
 *
 * The registry holds descriptors with 44 different `TConfig` types, and no single type argument
 * accepts them all — `TConfig` is covariant in `defaultConfig` but contravariant in `valueSchema`.
 * Rather than reach for `any` (which CLAUDE.md rules out), each descriptor is wrapped in these
 * type-erased operations, which take raw untyped config and do the parsing internally. Callers get
 * a safer API as a result: it is no longer possible to forget to parse config before use.
 */
export interface RegisteredField {
  readonly type: string;
  readonly label: string;
  readonly category: FieldCategory;
  readonly icon: string;
  readonly defaultConfig: Record<string, unknown>;
  readonly supportsProfileBinding: boolean;
  /** See `FieldDescriptor.profileParts`. Absent for scalar types. */
  readonly profileParts?: Readonly<Record<string, string>>;
  readonly defaultSearchable: boolean;
  /** Resolved, never absent — a descriptor that says nothing sorts as text. */
  readonly sortKind: FieldSortKind;
  readonly renderHints: RenderHints;
  /** Builds the answer schema from raw stored config, falling back to defaults if it is invalid. */
  readonly valueSchema: (rawConfig: unknown, isRequired: boolean) => z.ZodTypeAny;
  /** Checks stored config against this type's own schema — used by draft validation. */
  readonly validateConfig: (rawConfig: unknown) => { ok: true } | { ok: false; messages: string[] };
}

/**
 * The shape `eraseFieldType` works against.
 *
 * Every concrete descriptor is structurally compatible with this at runtime but not assignable to
 * it at compile time: `TConfig` sits in a contravariant position (`valueSchema`'s parameter), so no
 * supertype accepts all 44 of them. The single cast at the call site is where that unsoundness is
 * confined — and it is safe in practice because `eraseFieldType` only ever passes a descriptor the
 * output of *that same descriptor's* `configSchema`.
 */
export type ErasableFieldDescriptor = Omit<
  FieldDescriptor<Record<string, unknown>>,
  "valueSchema" | "configSchema"
> & {
  configSchema: z.ZodType<Record<string, unknown>, z.ZodTypeDef, unknown>;
  valueSchema: (config: Record<string, unknown>, isRequired: boolean) => z.ZodTypeAny;
};

export const eraseFieldType = (descriptor: ErasableFieldDescriptor): RegisteredField => {
  const parseOrDefault = (rawConfig: unknown): Record<string, unknown> => {
    const result = descriptor.configSchema.safeParse(rawConfig);
    return result.success ? result.data : descriptor.defaultConfig;
  };

  return {
    type: descriptor.type,
    label: descriptor.label,
    category: descriptor.category,
    icon: descriptor.icon,
    defaultConfig: descriptor.defaultConfig,
    supportsProfileBinding: descriptor.supportsProfileBinding,
    profileParts: descriptor.profileParts,
    defaultSearchable: descriptor.defaultSearchable,
    sortKind: descriptor.sortKind ?? "text",
    renderHints: descriptor.renderHints,
    valueSchema: (rawConfig, isRequired) =>
      descriptor.valueSchema(parseOrDefault(rawConfig), isRequired),
    validateConfig: (rawConfig) => {
      const result = descriptor.configSchema.safeParse(rawConfig);
      return result.success
        ? { ok: true }
        : { ok: false, messages: result.error.issues.map((issue) => issue.message) };
    },
  };
};

/**
 * Layout fields and identifiers take no applicant answer at all. Their `valueSchema` rejects any
 * value rather than ignoring one: a client sending a value for a `divider` or a `unique_id` is
 * either broken or probing, and the submit path should treat both the same way.
 */
export const noAnswerSchema = (): z.ZodTypeAny =>
  z.undefined({ invalid_type_error: "This field does not accept an answer" }).or(z.null());

/** Applies the required/optional rule uniformly, so no individual field module reimplements it. */
export const optionalUnlessRequired = (schema: z.ZodTypeAny, isRequired: boolean): z.ZodTypeAny => {
  if (isRequired) return schema;
  return schema.optional().nullable();
};

/** A non-empty string check that treats whitespace-only input as empty — required means answered. */
export const requiredText = (): z.ZodString => z.string().trim().min(1, "This field is required");
